import { useState } from "react";
import type { LibraryItem } from "../social";
import AuthedImg from "./AuthedImg";
import { matchesQuery, SearchField } from "./fields";

type Props = {
  items: LibraryItem[];
  onClose: () => void;
  onPick: (item: LibraryItem) => void;
  onUpload: (file: File) => Promise<LibraryItem>;
  onDelete: (id: string) => Promise<void>;
};

export default function MediaLibrary({ items, onClose, onPick, onUpload, onDelete }: Props) {
  const [over, setOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [askId, setAskId] = useState("");
  const [query, setQuery] = useState("");
  const shown = items.filter((item) => matchesQuery(`${item.name} ${item.kind}`, query));

  async function take(files: FileList | null) {
    const file = files?.[0];
    if (!file || sending) return;
    setSending(true);
    try {
      const item = await onUpload(file);
      onPick(item);
    } finally {
      setSending(false);
    }
  }

  async function remove(id: string) {
    setSending(true);
    try {
      await onDelete(id);
      setAskId("");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="pr-modal fixed inset-0 z-[55] flex items-center justify-center p-4">
      <div className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white">
        <div className="flex items-center justify-between px-5 pt-5">
          <h3 className="text-lg font-semibold">Médiathèque</h3>
          <button type="button" className="pr-btn-ghost" onClick={onClose}>
            Fermer
          </button>
        </div>
        <label
          className={`mx-5 mt-4 flex h-11 cursor-pointer items-center justify-center rounded-xl border border-dashed text-sm ${
            over ? "border-violet bg-violet/5" : "border-slate-200 bg-mist"
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void take(e.dataTransfer.files);
          }}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
            className="sr-only"
            onChange={(e) => {
              void take(e.target.files);
              e.target.value = "";
            }}
          />
          {sending ? "Envoi…" : "Déposer une photo ou une vidéo"}
        </label>
        <div className="px-5 pt-4">
          <SearchField value={query} onChange={setQuery} placeholder="Nom du fichier" />
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          {shown.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">
              {query.trim() ? "Aucun fichier pour cette recherche." : "Aucune photo pour l’instant."}
            </p>
          ) : (
            <ul className="pr-lib">
              {shown.map((item) => (
                <li key={item.id} className="pr-lib-item">
                  <button type="button" className="pr-lib-pick" onClick={() => onPick(item)}>
                    {item.kind === "image" ? (
                      <AuthedImg path={item.thumb || item.url} className="h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full items-center justify-center text-xs text-slate-500">Vidéo</span>
                    )}
                  </button>
                  {askId === item.id ? (
                    <div className="pr-lib-ask">
                      <p>Supprimer ?</p>
                      <div className="pr-lib-ask-row">
                        <button type="button" className="pr-lib-ask-yes" disabled={sending} onClick={() => void remove(item.id)}>
                          Retirer
                        </button>
                        <button type="button" className="pr-lib-ask-no" onClick={() => setAskId("")}>
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="pr-lib-del" aria-label="Supprimer" onClick={() => setAskId(item.id)}>
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
